import API from './services/api';
import React, { useState, useEffect, useRef } from 'react';
import DepartmentService from './services/departmentService';
import { X, AlertCircle, Printer, ArrowLeft, Save, PlusCircle } from 'lucide-react';
import { useAuth } from './AuthContext';
import './FormalHazardAssessment.css';
import { getDepartmentDisplayName } from './utils/departmentLabels';
import { getAppPath, isDemoMode } from './utils/runtimeMode';

const FORMAL_HAZARD_ASSESSMENT_AUTHORIZED_USERS = [
  'safety@diamondvalley.town',
  'sy1951901albertaltd@shaw.ca',
  'cao@diamondvalley.town',
  'doreen@davg.ca',
  'trevord@diamondvalley.town',
  'logans@diamondvalley.town',
  'firechief@diamondvalley.town'
];

const DEMO_FORMAL_HAZARD_ASSESSMENT_AUTHORIZED_USERS = [
  '3p3cao@gmail.com',
  'ctdemo.cao@proton.me'
];

function AssessmentStatusBar({ status }) {
  let label = '';
  let className = 'status-bar ';

  switch ((status || '').toUpperCase()) {
    case 'DRAFT':
      label = 'Draft (Not Submitted)';
      className += 'status-draft';
      break;
    case 'PENDING APPROVAL':
      label = 'Pending Supervisor/Delegate Approval';
      className += 'status-pending';
      break;
    case 'APPROVED':
      label = 'Approved';
      className += 'status-approved';
      break;
    case 'UPDATE REQUESTED':
      label = 'Update Requested';
      className += 'status-update-requested';
      break;
    case 'ARCHIVED':
      label = 'Archived';
      className += 'status-archived';
      break;
    default:
      label = status || 'Unknown';
      className += 'status-unknown';
  }

  return (
    <div className={className} style={{
      padding: '8px 16px',
      borderRadius: 4,
      marginBottom: 16,
      fontWeight: 'bold',
      background: '#f3f4f6',
      color: '#333',
      border: '1px solid #e5e7eb'
    }}>
      {label}
    </div>
  );
}

function ApprovalProgressBar({ status, isSupervisor, isDelegate, hasDelegate }) {
  // When a delegate is assigned, they act as supervisor with full approval authority
  // When no delegate, supervisor has full approval authority - no intermediate review step
  const steps = hasDelegate ? [
    { key: 'DRAFT', label: 'Draft' },
    { key: 'PENDING APPROVAL', label: 'Delegate Approval' },
    { key: 'APPROVED', label: 'Approved' },
    { key: 'ARCHIVED', label: 'Archived' }
  ] : [
    { key: 'DRAFT', label: 'Draft' },
    { key: 'PENDING APPROVAL', label: 'Supervisor Approval' },
    { key: 'APPROVED', label: 'Approved' },
    { key: 'ARCHIVED', label: 'Archived' }
  ];

  const currentStep = steps.findIndex(
    step => step.key === (status || '').toUpperCase()
  );

  return (
    <div className="approval-progress-bar" style={{ display: 'flex', marginBottom: 16 }}>
      {steps.map((step, idx) => (
        <div
          key={step.key}
          style={{
            flex: 1,
            padding: '8px 0',
            textAlign: 'center',
            background: idx === currentStep
              ? '#256029'
              : idx < currentStep
                ? '#dcfce7'
                : '#e5e7eb',
            color: idx === currentStep
              ? '#fff'
              : idx < currentStep
                ? '#166534'
                : '#888',
            borderRadius: idx === 0
              ? '4px 0 0 4px'
              : idx === steps.length - 1
                ? '0 4px 4px 0'
                : '0',
            fontWeight: idx === currentStep ? 'bold' : 'normal',
            borderRight: idx < steps.length - 1 ? '2px solid #fff' : 'none'
          }}
        >
          {step.label}
        </div>
      ))}
    </div>
  );
}

export default function FormalHazardAssessment() {
  // --- Authentication ---
  const { user: authUser, isAuthenticated } = useAuth();
  
  // --- State (must be declared before any conditional returns) ---
  const [view, setView] = useState('dashboard');
  const [currentAssessment, setCurrentAssessment] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [statusFilter, setStatusFilter] = useState('All');
  const [departmentManagement, setDepartmentManagement] = useState({ SUPERVISOR_EMAIL: '' });
  const [departmentSupervisor, setDepartmentSupervisor] = useState({ FIELD_SUPERVISOR: '' });
  
  // Import functionality state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importProgress, setImportProgress] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState('');
  
  // Create user object with consistent structure for the component
  const user = authUser ? {
    role: authUser.role || 'User',
    department: authUser.department || 'Operations',
    username: authUser.username || authUser.email || 'unknown@diamondvalley.town'
  } : {
    role: 'User',
    department: 'Operations', 
    username: 'unknown@diamondvalley.town'
  };

  const departments = [
    'Parks', 'Recreation', 'Roads', 'Utilities', 'Communications', 'Finance',
    'Municipal Enforcement',
    'Planning and Development', 'Legislative Services', 'Operations', 'Administration'
  ];

  // --- Helper: Get Approver Email ---
  function getApproverEmail(department, departmentManagement) {
    // New rule: a single approver handles all departments.
    // Keep the signature (department, departmentManagement) to avoid changing callers.
    return isDemoMode() ? '3p3cao@gmail.com' : 'safety@diamondvalley.town';
  }

  // --- Permission Logic ---
  // Authorized users who can access and manage this module
  const authorizedUsers = isDemoMode()
    ? [...FORMAL_HAZARD_ASSESSMENT_AUTHORIZED_USERS, ...DEMO_FORMAL_HAZARD_ASSESSMENT_AUTHORIZED_USERS]
    : FORMAL_HAZARD_ASSESSMENT_AUTHORIZED_USERS;
  const isAuthorizedUser = authorizedUsers.includes(user.username?.toLowerCase());
  const canManage = isAuthenticated && isAuthorizedUser;

  // --- Helper: Get Archived Date ---
  const getArchivedDate = (assessment) => {
    // First, try to find an explicit 'Archived' entry in approval history
    if (assessment.approvalHistory && Array.isArray(assessment.approvalHistory)) {
      const archivedEntry = assessment.approvalHistory
        .filter(entry => entry.action === 'Archived')
        .sort((a, b) => new Date(b.date) - new Date(a.date))[0];
      
      if (archivedEntry) {
        return archivedEntry.date;
      }
    }
    
    // For assessments that were archived before the approval history fix,
    // use the approval date if the status is 'Archived'
    if (assessment.status === 'Archived' && assessment.approvalDate) {
      return assessment.approvalDate;
    }
    
    // Fallback to 'N/A'
    return 'N/A';
  };

  // --- Fetch Assessments ---
  const fetchAssessments = async () => {
    console.log('Fetching assessments from API...');
    try {
      const response = await API.get('/api/formalHazardAssessments');
      console.log('Fetch assessments response status:', response.status);
      const data = response.data;
      console.log('Raw assessments data:', data);
      const processedData = Array.isArray(data)
        ? data.map(a => ({
          ...a,
          id: a.id || a.ID,
          title: a.title || a.TITLE,
          department: a.department || a.DEPARTMENT,
          reviewDate: a.reviewDate || a.REVIEW_DATE,
          status: a.status || a.STATUS,
          approvalDate: a.approvalDate || a.APPROVAL_DATE,
          delegate: a.delegate || a.DELEGATE || '',
          approvalHistory: a.approvalHistory || a.APPROVAL_HISTORY || [],
        }))
        : [];
      console.log('Processed assessments data:', processedData);
      setAssessments(processedData);
    } catch (error) {
      console.error('Error fetching assessments:', error);
      setAssessments([]);
    }
  };

  // --- Fetch a single assessment (detail view) ---
  const fetchAssessment = async (id) => {
    const response = await API.get(`/api/formalHazardAssessments/${id}`);
    if (response.status >= 200 && response.status < 300) {
      const data = response.data;
      let notes = [];
      if (Array.isArray(data.notes) && data.notes.length > 0) {
        notes = data.notes;
      } else if (Array.isArray(data.NOTES) && data.NOTES.length > 0) {
        notes = data.NOTES;
      } else if (typeof data.NOTES === 'string' && data.NOTES.trim().startsWith('[')) {
        try { notes = JSON.parse(data.NOTES); } catch { notes = []; }
      }
      setCurrentAssessment({
        id: data.id || data.ID,
        title: data.title || data.TITLE,
        department: data.department || data.DEPARTMENT,
        jobPositions: data.jobPositions || data.JOB_POSITIONS,
        description: data.description || data.DESCRIPTION,
        tasks: data.tasks || data.TASKS,
        reviewDate: data.reviewDate || data.REVIEW_DATE,
        status: data.status || data.STATUS,
        approvedBy: data.approvedBy || data.APPROVED_BY,
        approvalDate: data.approvalDate || data.APPROVAL_DATE,
        notes,
        delegate: data.delegate || data.DELEGATE || '',
        approvalHistory: data.approvalHistory || data.APPROVAL_HISTORY || [],
      });
    }
  };

  // --- Effects (must be before any conditional returns) ---
  useEffect(() => {
    fetchAssessments();
  }, []);

  // --- Direct Link Handling ---
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('id');
    if (id) {
      (async () => {
        await fetchAssessment(id);
        setView('view');
      })();
    }
  }, []);

  useEffect(() => {
    if (currentAssessment?.department) {
      DepartmentService.getDepartmentManagement(currentAssessment.department)
        .then(data => setDepartmentManagement(data || { SUPERVISOR_EMAIL: '' }));
    }
  }, [currentAssessment?.department]);

  // Add this effect to fetch the FIELD_SUPERVISOR for the department
  useEffect(() => {
    if (currentAssessment?.department) {
      API.get(`/api/users?role=FIELD_SUPERVISOR&department=${encodeURIComponent(currentAssessment.department)}`)
        .then(res => {
          const supervisor = (res.data.users && res.data.users[0]) || { email: '' };
          setDepartmentSupervisor({ FIELD_SUPERVISOR: supervisor.email });
        })
        .catch(() => setDepartmentSupervisor({ FIELD_SUPERVISOR: '' }));
    }
  }, [currentAssessment?.department]);

  // --- Access Control: Only safety@diamondvalley.town can access this module ---
  if (!isAuthenticated) {
    return (
      <div className="app-container">
        <div className="main-content">
          <div className="form-content" style={{ textAlign: 'center', padding: '2rem' }}>
            <h2>Authentication Required</h2>
            <p>Please log in to access this page.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthorizedUser) {
    return (
      <div className="app-container">
        <div className="main-content">
          <div className="form-content" style={{ textAlign: 'center', padding: '2rem' }}>
            <AlertCircle style={{ width: '4rem', height: '4rem', color: '#dc2626', margin: '0 auto 1rem' }} />
            <h2 style={{ color: '#dc2626', marginBottom: '1rem' }}>Access Restricted</h2>
            <p style={{ color: '#6b7280', marginBottom: '0.5rem' }}>
              This module is restricted to H&S Advisor access only.
            </p>
            <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
              Current user: {user.username}
            </p>
            <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
              If you need access to this module, please contact the H&S Advisor.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // --- Create New Assessment ---
  const createNewAssessment = () => {
    const newAssessment = {
      title: '',
      department: user.department || departments[0], // Ensure department is set
      jobPositions: [],
      description: '',
      tasks: [],
      reviewDate: '',
      status: 'Draft',
      approvedBy: '',
      approvalDate: '',
      notes: [],
      delegate: '',
      approvalHistory: [],
    };
    setCurrentAssessment(newAssessment);
    setView('new');
  };

  // --- Duplicate Assessment ---
  const duplicateAssessment = (assessment) => {
    const duplicated = {
      ...assessment,
      id: undefined,
      title: `${assessment.title} (Copy)`,
      status: 'Draft',
      approvedBy: '',
      approvalDate: '',
      notes: [],
      delegate: '',
      approvalHistory: [],
    };
    setCurrentAssessment(duplicated);
    setView('new');
  };

  // --- Archive Assessment ---
  const archiveAssessment = async (assessment) => {
    const confirmed = window.confirm(
      `Are you sure you want to archive the assessment "${assessment.title}"? This action can be undone by an administrator.`
    );
    if (!confirmed) return;

    // Use changeStatus to properly handle approval history
    await changeStatus(
      assessment.id, 
      'Archived', 
      user.username, 
      new Date().toISOString().split('T')[0], 
      assessment.delegate || ''
    );
    fetchAssessments();
  };

  // --- Edit/View Assessment ---
  const editAssessment = async (assessment) => {
    await fetchAssessment(assessment.id);
    setView('edit');
  };

  const viewAssessment = async (assessment) => {
    if (!assessment.id) {
      alert('Assessment has no id!');
      return;
    }
    // Update the URL with the id query parameter
    const url = `/formal-hazard-assessment?id=${assessment.id}`;
    window.history.pushState({}, '', getAppPath(url));

    await fetchAssessment(assessment.id);
    setView('view');
  };

  // --- Save Assessment (with approval history) ---
  const saveAssessment = async (assessment) => {
    let response;
    if (assessment.id) {
      response = await API.put(`/api/formalHazardAssessments/${assessment.id}`, assessment);
    } else {
      response = await API.post('/api/formalHazardAssessments', assessment);
      if (response.status >= 200 && response.status < 300) {
        const data = response.data;
        if (data.id) {
          setCurrentAssessment(prev => ({ ...prev, id: data.id }));
          await fetchAssessment(data.id);
        }
      }
    }
    if (response.status >= 200 && response.status < 300) {
      await fetchAssessments();
      setView('dashboard');
      setCurrentAssessment(null);
    }
  };

  // --- Change Status (with approval history) ---
  const changeStatus = async (id, status, approvedBy = '', approvalDate = '', delegate = '', addHistory = true) => {
    try {
      console.log('=== FRONTEND STATUS CHANGE DEBUG ===');
      console.log('Assessment ID:', id);
      console.log('New Status:', status);
      console.log('ApprovedBy:', approvedBy);
      console.log('Delegate:', delegate);
      console.log('=====================================');

      // Fetch current assessment to get approvalHistory
      let approvalHistory = [];
      const assessment = assessments.find(a => a.id === id) || currentAssessment;
      if (assessment && assessment.approvalHistory) {
        approvalHistory = [...assessment.approvalHistory];
      }
      if (addHistory) {
        approvalHistory.push({
          action: status,
          by: approvedBy,
          date: approvalDate || new Date().toISOString().split('T')[0],
          delegate: delegate || null
        });
      }

      console.log('Sending PATCH request to:', `/api/formalHazardAssessments/${id}/status`);
      const response = await API.patch(`/api/formalHazardAssessments/${id}/status`, {
        status, approvedBy, approvalDate, delegate, approvalHistory
      });

      const responseData = response.data;
      console.log('PATCH response:', responseData);

      // Add a small delay to ensure backend processing is complete
      await new Promise(resolve => setTimeout(resolve, 500));

      // Refresh data after successful update
      console.log('Refreshing assessment data...');
      await fetchAssessment(id);
      await fetchAssessments();
      
      console.log('Status change completed successfully');
    } catch (error) {
      console.error('Error in changeStatus:', error);
      alert('An error occurred while updating the assessment status.');
    }
  };

  // --- Add Note ---
  const addNote = async (id, note) => {
    await API.post(`/api/formalHazardAssessments/${id}/notes`, note);
    await fetchAssessment(id);
  };

  // --- Risk Calculation ---
  const calculateRiskRating = (severity, likelihood, frequency) => {
    return Number(severity) + Number(likelihood) + Number(frequency);
  };

  const getRiskClass = (rating) => {
    if (rating >= 15) return 'risk-critical';
    if (rating >= 12) return 'risk-high';
    if (rating >= 9) return 'risk-moderate';
    if (rating >= 6) return 'risk-low-moderate';
    return 'risk-low';
  };

  const getRiskLabel = (rating) => {
    if (rating >= 15) return 'Critical Risk';
    if (rating >= 12) return 'High Risk';
    if (rating >= 9) return 'Moderate Risk';
    if (rating >= 6) return 'Low-Moderate Risk';
    return 'Low Risk';
  };

  // --- Import Functions ---
  const previewReports = async (department = '') => {
    try {
      setImportProgress('Loading preview...');
      const url = department 
        ? `/api/formalHazardAssessments/preview-reports/${department}`
        : '/api/formalHazardAssessments/preview-reports';
      
      const response = await API.get(url);
      const data = response.data;
      
      if (data.success) {
        setPreviewData(data.assessments);
        setImportProgress(null);
      } else {
        throw new Error(data.error || 'Failed to preview reports');
      }
    } catch (error) {
      console.error('Error previewing reports:', error);
      setImportProgress(null);
      alert(`Error previewing reports: ${error.message}`);
    }
  };

  const importReports = async (department = '', importAll = false) => {
    try {
      setImportProgress('Importing reports...');
      
      const response = await API.post('/api/formalHazardAssessments/import-reports', { department, importAll });
      const data = response.data;
      
      if (data.success) {
        setImportProgress(null);
        setShowImportModal(false);
        setPreviewData(null);
        alert(`Successfully imported ${data.imported} assessments from ${data.total} Excel files!`);
        await fetchAssessments(); // Refresh the assessments list
      } else {
        throw new Error(data.error || 'Failed to import reports');
      }
    } catch (error) {
      console.error('Error importing reports:', error);
      setImportProgress(null);
      alert(`Error importing reports: ${error.message}`);
    }
  };

  // --- Filtered Assessments ---
  const filteredAssessments = assessments.filter(a => {
    if (statusFilter === 'All') return a.status !== 'Archived';
    if (statusFilter === 'Archived') return a.status === 'Archived';
    return a.status === statusFilter;
  });

  // --- Approval Logic ---
  const isSupervisor = user.username && departmentSupervisor.FIELD_SUPERVISOR &&
    user.username.toLowerCase() === departmentSupervisor.FIELD_SUPERVISOR.toLowerCase();

  // --- Dashboard Render ---
  const renderDashboard = () => (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h2 className="dashboard-title">Formal Hazard Assessments</h2>
        <div style={{ display: 'flex', gap: '10px' }}>
          {canManage && (
            <>
              <button
                onClick={createNewAssessment}
                className="btn btn-primary btn-with-icon"
              >
                <PlusCircle className="icon-sm" />
                New Assessment
              </button>
              <button
                onClick={() => setShowImportModal(true)}
                className="btn btn-secondary btn-with-icon"
                style={{ backgroundColor: '#6366f1', color: 'white' }}
              >
                📊 Import from Excel
              </button>
            </>
          )}
        </div>
      </div>
      <div className="filter-controls">
        <label>Show: </label>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="All">All</option>
          <option value="Active">Active</option>
          <option value="Draft">Draft</option>
          <option value="Archived">Archived</option>
        </select>
      </div>
      <div className="table-container">
        <table className="assessment-table">
          <thead className="table-header">
            <tr>
              <th className="table-cell table-cell-header">Title</th>
              <th className="table-cell table-cell-header">Department</th>
              <th className="table-cell table-cell-header">Risk Level</th>
              <th className="table-cell table-cell-header">Next Review Date</th>
              <th className="table-cell table-cell-header">Status</th>
              <th className="table-cell table-cell-header">Actions</th>
            </tr>
          </thead>
          <tbody className="table-body">
            {filteredAssessments.map((assessment) => {
              const highestRisk = (assessment.tasks || []).reduce((max, task) =>
                Math.max(max, task.riskRating), 0);

              return (
                <tr key={assessment.id} className="table-row">
                  <td className="table-cell" data-label="Title">{assessment.title}</td>
                  <td className="table-cell" data-label="Department">{getDepartmentDisplayName(assessment.department)}</td>
                  <td className="table-cell" data-label="Risk Level">
                    <span className={`risk-badge ${getRiskClass(highestRisk)}`}>
                      {getRiskLabel(highestRisk)}
                    </span>
                  </td>
                  <td className="table-cell" data-label="Next Review Date">
                    <div className="review-date-display">
                      <span className="review-date-value">{assessment.reviewDate}</span>
                      <span className={`review-period-indicator ${
                        highestRisk >= 15 ? 'critical' : 
                        highestRisk >= 12 ? 'high' : 
                        highestRisk >= 9 ? 'moderate' : 'low'
                      }`}>
                        {highestRisk >= 15 ? '6mo' : 
                         highestRisk >= 12 ? '1yr' : 
                         highestRisk >= 9 ? '1yr' : '2yr'}
                      </span>
                    </div>
                  </td>
                  <td className="table-cell" data-label="Status">
                    {assessment.status}
                    {assessment.status === 'Pending Approval' && (
                      <span className="badge badge-warning" style={{ marginLeft: 8 }}>
                        Needs Approval
                      </span>
                    )}
                  </td>
                  <td className="table-cell" data-label="Actions">
                    <div className="action-buttons">
                      <button
                        onClick={() => viewAssessment(assessment)}
                        className="btn-link btn-view"
                      >
                        View
                      </button>
                      {canManage && assessment.status !== 'Archived' && (
                        <>
                          <button
                            onClick={() => editAssessment(assessment)}
                            className="btn-link btn-edit"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => duplicateAssessment(assessment)}
                            className="btn-link btn-duplicate"
                          >
                            Duplicate
                          </button>
                          <button
                            onClick={() => archiveAssessment(assessment)}
                            className="btn-link btn-archive"
                          >
                            Archive
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="archived-section">
        <h3 className="section-title">Archived Assessments</h3>
        <div className="table-container">
          <table className="assessment-table">
            <thead className="table-header">
              <tr>
                <th className="table-cell table-cell-header">Title</th>
                <th className="table-cell table-cell-header">Department</th>
                <th className="table-cell table-cell-header">Archived Date</th>
                <th className="table-cell table-cell-header">Actions</th>
              </tr>
            </thead>
            <tbody className="table-body">
              {assessments.filter(a => a.status === 'Archived').map((assessment) => (
                <tr key={assessment.id} className="table-row">
                  <td className="table-cell" data-label="Title">{assessment.title}</td>
                  <td className="table-cell" data-label="Department">{getDepartmentDisplayName(assessment.department)}</td>
                  <td className="table-cell" data-label="Archived Date">{getArchivedDate(assessment)}</td>
                  <td className="table-cell" data-label="Actions">
                    <button
                      onClick={() => viewAssessment(assessment)}
                      className="btn-link btn-view"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Import Modal */}
      {showImportModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '8px',
            maxWidth: '80%',
            maxHeight: '80%',
            overflow: 'auto',
            minWidth: '600px'
          }}>
            <h3>Import Excel Reports as Formal Hazard Assessments</h3>
            
            <div style={{ marginBottom: '1rem' }}>
              <label>Select Department (or leave blank for all):</label>
              <select 
                value={selectedDepartment} 
                onChange={e => setSelectedDepartment(e.target.value)}
                style={{ marginLeft: '10px', padding: '5px' }}
              >
                <option value="">All Departments</option>
                <option value="admin">Administration</option>
                <option value="parks">Parks</option>
                <option value="recreation">Recreation</option>
                <option value="roads">Roads and Operations</option>
                <option value="utilities">Utilities</option>
              </select>
            </div>
            
            <div style={{ marginBottom: '1rem', display: 'flex', gap: '10px' }}>
              <button 
                onClick={() => previewReports(selectedDepartment)}
                disabled={!!importProgress}
                className="btn btn-secondary"
              >
                📋 Preview Reports
              </button>
              <button 
                onClick={() => importReports(selectedDepartment, !selectedDepartment)}
                disabled={!!importProgress}
                className="btn btn-primary"
              >
                📊 Import Reports
              </button>
            </div>
            
            {importProgress && (
              <div style={{ padding: '10px', backgroundColor: '#f0f0f0', borderRadius: '4px', marginBottom: '1rem' }}>
                {importProgress}
              </div>
            )}
            
            {previewData && (
              <div style={{ marginBottom: '1rem' }}>
                <h4>Preview ({previewData.length} assessments will be created):</h4>
                <div style={{ maxHeight: '300px', overflow: 'auto', border: '1px solid #ddd', padding: '10px' }}>
                  {previewData.map((assessment, index) => (
                    <div key={index} style={{ 
                      borderBottom: '1px solid #eee', 
                      paddingBottom: '10px', 
                      marginBottom: '10px' 
                    }}>
                      <strong>{assessment.title}</strong> ({getDepartmentDisplayName(assessment.department)})
                      <div style={{ fontSize: '0.9em', color: '#666' }}>
                        Tasks: {assessment.tasks?.length || 0}, 
                        Job Positions: {assessment.jobPositions?.join(', ') || 'None'}
                      </div>
                      {assessment.tasks?.[0] && (
                        <div style={{ fontSize: '0.8em', color: '#888' }}>
                          Risk: {assessment.tasks[0].riskRating || 'TBD'}, 
                          Controls: {assessment.tasks[0].controls ? 'Yes' : 'None'}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button 
                onClick={() => {
                  setShowImportModal(false);
                  setPreviewData(null);
                  setImportProgress(null);
                  setSelectedDepartment('');
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // --- Assessment Form Component ---
  const AssessmentForm = ({ assessment, setAssessment, mode, user }) => {
    const isReadOnly = mode === 'view';
    const [localAssessment, setLocalAssessment] = useState({ ...assessment });
    const [noteInput, setNoteInput] = useState('');
    const [noteType, setNoteType] = useState('note');
    const [departmentUsers, setDepartmentUsers] = useState([]);
    const [jobPositionOptions, setJobPositionOptions] = useState([]);
    const [isApproving, setIsApproving] = useState(false);
    const printRef = useRef();

    // Check if user is in the authorized safety/leadership group
    const isSafety = authorizedUsers.includes(user.username?.toLowerCase());

    // Keep localAssessment in sync with assessment prop
    useEffect(() => {
      const processedAssessment = { ...assessment };
      
      // Parse existing controls into separate fields for each task
      if (processedAssessment.tasks) {
        processedAssessment.tasks = processedAssessment.tasks.map(task => {
          const processedTask = { ...task };
          
          // If we have controls but no individual control fields, parse them
          if (task.controls && (!task.engControls && !task.adminControls && !task.ppeControls)) {
            const controls = task.controls;
            
            // Extract ENG controls
            const engMatch = controls.match(/ENG:\s*([^]*?)(?=\n\s*(?:ADMIN:|PPE:)|$)/i);
            processedTask.engControls = engMatch ? engMatch[1].trim() : '';
            
            // Extract ADMIN controls
            const adminMatch = controls.match(/ADMIN:\s*([^]*?)(?=\n\s*(?:ENG:|PPE:)|$)/i);
            processedTask.adminControls = adminMatch ? adminMatch[1].trim() : '';
            
            // Extract PPE controls
            const ppeMatch = controls.match(/PPE:\s*([^]*?)(?=\n\s*(?:ENG:|ADMIN:)|$)/i);
            processedTask.ppeControls = ppeMatch ? ppeMatch[1].trim() : '';
          }
          
          return processedTask;
        });
      }
      
      setLocalAssessment(processedAssessment);
    }, [assessment]);

    // --- DEBUG LOG: Track department changes and effect firing ---
    useEffect(() => {
      console.log('[DEBUG] AssessmentForm useEffect fired. localAssessment.department:', localAssessment.department);
      let isMounted = true;
      async function fetchDepartmentUsers() {
        if (!localAssessment.department) {
          console.log('[DEBUG] No department set, skipping fetch.');
          if (isMounted) setDepartmentUsers([]);
          return;
        }
        try {
          console.log('[DEBUG] Fetching users for department:', localAssessment.department);
          const res = await API.get(`/api/users?department=${encodeURIComponent(localAssessment.department)}`);
          const data = res.data;
          const users = data && Array.isArray(data.users) ? data.users : [];
          console.log('[DEBUG] Users fetched:', users);
          if (isMounted) setDepartmentUsers(users);
        } catch (err) {
          console.log('[DEBUG] Error fetching department users:', err);
          if (isMounted) setDepartmentUsers([]);
        }
      }
      fetchDepartmentUsers();
      return () => { isMounted = false; };
    }, [localAssessment.department]);

    // --- Delegate selection (authorized safety/leadership group only, not supervisor/approver) ---
    const approverEmail = getApproverEmail(
      localAssessment?.department || '',
      departmentManagement || {}
    ) || '';
    const isApprover = user.username && approverEmail && user.username.toLowerCase() === approverEmail.toLowerCase();
    const isDelegate = localAssessment.delegate && user.username.toLowerCase() === localAssessment.delegate.toLowerCase();
    const canAssignDelegate = isSafety && !isApprover;
    
    // If a delegate is assigned, only the delegate can approve (not the supervisor)
    // If no delegate is assigned, only the supervisor can approve
    // Authorized safety/leadership users can always approve as they have oversight authority
    const canApprove = isSafety || (localAssessment.delegate ? isDelegate : isApprover);

    // Print handling: expand textareas into printable blocks so full text is visible
    const printReplacementsRef = useRef([]);

    const expandTextareasForPrint = () => {
      try {
        const selector = 'textarea.form-textarea, textarea.control-textarea, textarea.print-only';
        const textareas = Array.from(document.querySelectorAll(selector));
        printReplacementsRef.current = textareas.map(td => {
          const wrapper = document.createElement('div');
          wrapper.className = 'print-textarea-replacement';
          // copy text content, preserving newlines
          wrapper.textContent = td.value || td.textContent || '';
          // copy computed font and spacing to make printed output look similar
          wrapper.style.whiteSpace = 'pre-wrap';
          wrapper.style.fontFamily = window.getComputedStyle(td).fontFamily || 'inherit';
          wrapper.style.fontSize = window.getComputedStyle(td).fontSize || 'inherit';
          wrapper.style.lineHeight = window.getComputedStyle(td).lineHeight || '1.2';
          wrapper.style.padding = '0.25rem 0';
          wrapper.style.margin = window.getComputedStyle(td).margin || '0';
          // hide the original textarea and insert replacement
          td.style.display = 'none';
          td.parentNode && td.parentNode.insertBefore(wrapper, td.nextSibling);
          return { original: td, replacement: wrapper };
        });
      } catch (err) {
        console.error('Error preparing print replacements:', err);
      }
    };

    const restoreTextareasAfterPrint = () => {
      try {
        (printReplacementsRef.current || []).forEach(pair => {
          try {
            if (pair.replacement && pair.replacement.parentNode) pair.replacement.parentNode.removeChild(pair.replacement);
            if (pair.original) pair.original.style.display = '';
          } catch (e) {
            // ignore per-item errors
          }
        });
      } catch (err) {
        console.error('Error restoring textareas after print:', err);
      } finally {
        printReplacementsRef.current = [];
      }
    };

    // Print handler
    const handlePrint = () => {
      expandTextareasForPrint();
      // browsers will also fire beforeprint/afterprint events; ensure restore after a short timeout as fallback
      try {
        window.print();
      } finally {
        // setTimeout to allow print dialog to be shown before restoring in browsers that do not fire afterprint reliably
        setTimeout(() => {
          restoreTextareasAfterPrint();
        }, 500);
      }
    };

    // Attach global beforeprint/afterprint to support ctrl+P and other triggers
    useEffect(() => {
      const before = () => expandTextareasForPrint();
      const after = () => restoreTextareasAfterPrint();
      window.addEventListener('beforeprint', before);
      window.addEventListener('afterprint', after);
      // media query fallback for older browsers
      const mql = window.matchMedia && window.matchMedia('print');
      if (mql && mql.addListener) mql.addListener((ev) => { if (!ev.matches) restoreTextareasAfterPrint(); });
      return () => {
        window.removeEventListener('beforeprint', before);
        window.removeEventListener('afterprint', after);
        if (mql && mql.removeListener) mql.removeListener(() => {});
      };
    }, []);

    const addTask = () => {
      const newTask = {
        task: '',
        hazards: '',
        severity: 1,
        likelihood: 1,
        frequency: 1,
        riskRating: 3,
        controls: ''
      };

      setLocalAssessment({
        ...localAssessment,
        tasks: [...(localAssessment.tasks || []), newTask]
      });
    };

    const updateTask = (index, field, value) => {
      const updatedTasks = [...(localAssessment.tasks || [])];
      updatedTasks[index] = {
        ...updatedTasks[index],
        [field]: value
      };

      if (['severity', 'likelihood', 'frequency'].includes(field)) {
        const { severity, likelihood, frequency } = updatedTasks[index];
        updatedTasks[index].riskRating = calculateRiskRating(
          Number(severity),
          Number(likelihood),
          Number(frequency)
        );
      }

      setLocalAssessment({
        ...localAssessment,
        tasks: updatedTasks
      });
    };

    const updateTaskControl = (index, controlType, value) => {
      const updatedTasks = [...(localAssessment.tasks || [])];
      const task = { ...updatedTasks[index] };
      
      // Update the individual control field (for UI purposes only)
      task[controlType] = value;
      
      // Combine all controls into the main controls field
      const engControls = task.engControls ? `ENG: ${task.engControls}` : '';
      const adminControls = task.adminControls ? `ADMIN: ${task.adminControls}` : '';
      const ppeControls = task.ppeControls ? `PPE: ${task.ppeControls}` : '';
      
      const combinedControls = [engControls, adminControls, ppeControls]
        .filter(control => control.trim() !== '' && !control.endsWith(': '))
        .join('\n\n');
      
      task.controls = combinedControls;
      
      // Remove individual control fields from the task object that gets saved to database
      // Keep them only in local state for UI purposes
      updatedTasks[index] = task;

      setLocalAssessment({
        ...localAssessment,
        tasks: updatedTasks
      });
    };

    const removeTask = (index) => {
      const updatedTasks = [...(localAssessment.tasks || [])];
      updatedTasks.splice(index, 1);
      setLocalAssessment({
        ...localAssessment,
        tasks: updatedTasks
      });
    };

    // Calculate auto review date for validation
    // Enhanced Automatic Review Date Setting Based on Risk Level
    const getAutoReviewDate = (tasks) => {
      const highestRisk = (tasks || []).reduce((max, task) =>
        Math.max(max, task.riskRating), 0);
      
      let reviewPeriod = 2; // Default: 2 years for low risk
      let reviewReason = 'Low Risk (Below 9)';
      
      if (highestRisk >= 15) {
        reviewPeriod = 0.5; // 6 months for Critical Risk
        reviewReason = 'Critical Risk (15+)';
      } else if (highestRisk >= 12) {
        reviewPeriod = 1; // 1 year for High Risk
        reviewReason = 'High Risk (12-14)';
      } else if (highestRisk >= 9) {
        reviewPeriod = 1; // 1 year for Moderate Risk
        reviewReason = 'Moderate Risk (9-11)';
      }
      
      const today = new Date();
      const reviewDate = new Date(today);
      
      if (reviewPeriod < 1) {
        // For periods less than 1 year, use months
        reviewDate.setMonth(today.getMonth() + Math.round(reviewPeriod * 12));
      } else {
        reviewDate.setFullYear(today.getFullYear() + reviewPeriod);
      }
      
      return { date: reviewDate, period: reviewPeriod, reason: reviewReason, riskLevel: highestRisk };
    };
    
    const autoReviewInfo = getAutoReviewDate(localAssessment.tasks || []);
    const autoReviewDate = autoReviewInfo.date;
    const autoReviewDateStr = autoReviewDate.toISOString().split('T')[0];

    // Helper function to get review period display text
    const getReviewPeriodText = (period) => {
      if (period === 0.5) return '6 months';
      if (period === 1) return '1 year';
      if (period === 2) return '2 years';
      return `${period} year${period !== 1 ? 's' : ''}`;
    };

    // Handler for review date change
    const handleReviewDateChange = (e) => {
      if (!isSafety) return; // Only allow authorized safety/leadership users to change
      const value = e.target.value;
      if (value > autoReviewDateStr) {
        alert(`Review date cannot be later than the recommended date (${autoReviewDateStr}).`);
        return;
      }
      setLocalAssessment({ ...localAssessment, reviewDate: value });
    };

    // PATCH: Only allow authorized safety/leadership users to set review date, and respect their chosen date if valid
    const handleSubmit = async (e, isDraft = false) => {
      if (e) e.preventDefault();

      // Critical Risk Validation - Block submission if critical risk exists
      if (!isDraft && hasCriticalRisk) {
        alert('Cannot submit assessment: Critical risk (15+) detected. Please reassess controls to reduce risk levels before submission.');
        return;
      }

      const highestRisk = (localAssessment.tasks || []).reduce((max, task) =>
        Math.max(max, task.riskRating), 0);

      let reviewPeriod = 2;
      if (highestRisk >= 9) reviewPeriod = 1;

      const today = new Date();
      const autoReviewDate = new Date(today);
      autoReviewDate.setFullYear(today.getFullYear() + reviewPeriod);
      const autoReviewDateStr = autoReviewDate.toISOString().split('T')[0];

      // Only allow authorized safety/leadership users to set a custom review date
      let reviewDateToSave = autoReviewDateStr;
      if (
        isSafety &&
        localAssessment.reviewDate &&
        localAssessment.reviewDate <= autoReviewDateStr
      ) {
        reviewDateToSave = localAssessment.reviewDate;
      }

      // --- Approval history logic ---
      const approvalHistory = [
        ...(localAssessment.approvalHistory || []),
        {
          action: isDraft ? 'Saved as Draft' : 'Submitted',
          by: user.username,
          date: today.toISOString().split('T')[0],
          delegate: localAssessment.delegate || null
        }
      ];

      // Clean tasks data - remove individual control fields before saving to database
      const cleanedTasks = (localAssessment.tasks || []).map(task => {
        const { engControls, adminControls, ppeControls, ...cleanTask } = task;
        return cleanTask;
      });

      const finalAssessment = {
        ...localAssessment,
        tasks: cleanedTasks,
        reviewDate: reviewDateToSave,
        status: isDraft ? 'Draft' : 'Pending Approval',
        approvedBy: isDraft ? '' : user.role,
        approvalDate: isDraft ? '' : today.toISOString().split('T')[0],
        approvalHistory,
      };

      await saveAssessment(finalAssessment);
    };

    const hasCriticalRisk = (localAssessment.tasks || []).some(t => t.riskRating >= 15);
    const departmentOptions = Array.from(
      new Set([...(Array.isArray(departments) ? departments : []), localAssessment.department].filter(Boolean))
    );

    // --- Static job positions mapping by department ---
    const getJobPositionsByDepartment = (department) => {
      const jobPositionsMap = {
        'Parks': [
          'Parks Operator',
          'Parks Supervisor',
          'Facility Maintenance'
        ],
        'Recreation': [
          'Recreation Operator',
          'Recreation Supervisor'
        ],
        'Roads': [
          'Roads Operator',
          'Roads Supervisor',
          'Fleet Maintenance Technician',
          'Operations Manager'
        ],
        'Utilities': [
          'Water/Wastewater Operator',
          'Labourer',
          'Utilities Supervisor'
        ]
      };
      
      return jobPositionsMap[department] || [];
    };

    // --- Fetch job positions when department changes ---
    useEffect(() => {
      if (!localAssessment.department) {
        setJobPositionOptions([]);
        return;
      }
      
      const positions = getJobPositionsByDepartment(localAssessment.department);
      setJobPositionOptions(positions);
      console.log(`Job positions for ${localAssessment.department}:`, positions);
    }, [localAssessment.department]);

    return (
      <div className="form-container">
        {/* Print Button */}
        {mode === 'view' && (
          <div style={{ marginBottom: 16 }}>
            <button
              className="btn btn-secondary btn-sm"
              type="button"
              onClick={handlePrint}
            >
              <Printer className="icon-sm" /> Print
            </button>
          </div>
        )}

        {/* Status Bar and Approval Info */}
        <AssessmentStatusBar status={localAssessment.status} />

        <ApprovalProgressBar
          status={localAssessment.status}
          isSupervisor={isSupervisor}
          isDelegate={isDelegate}
          hasDelegate={!!localAssessment.delegate}
        />

        {(canApprove) && (
          <div style={{ marginBottom: 8, color: '#256029', fontWeight: 'bold' }}>
            You can approve this assessment as {
              isSafety ? 'Authorized User' : 
              isDelegate ? 'Delegate (acting for supervisor)' : 
              'Supervisor'
            }.
          </div>
        )}

        {/* Place the banner here, before the form */}
        {localAssessment.status === 'Pending Approval' && (
          <div style={{
            background: '#fff3cd',
            color: '#856404',
            padding: '12px 16px',
            borderRadius: 4,
            marginBottom: 16,
            fontWeight: 'bold',
            border: '1px solid #ffeeba'
          }}>
            This assessment is awaiting supervisor approval.
          </div>
        )}

        <div className="form-header">
          <div className="form-header-left">
            <button
              onClick={() => setView('dashboard')}
              className="btn-back"
            >
              <ArrowLeft className="icon-sm" />
            </button>
            <h2 className="form-title">
              {mode === 'new' ? 'New Hazard Assessment' :
                mode === 'edit' ? 'Edit Hazard Assessment' : 'View Hazard Assessment'}
            </h2>
          </div>
        </div>

        {/* Printable Content */}
        <div className="print-area" ref={printRef}>
          {/* Global Critical Risk Banner */}
          {hasCriticalRisk && (
            <div className="critical-risk-banner">
              <div className="critical-risk-banner-content">
                <AlertCircle className="critical-risk-banner-icon" />
                <div className="critical-risk-banner-text">
                  <h3 className="critical-risk-banner-title">⚠️ CRITICAL RISK ASSESSMENT ALERT</h3>
                  <p className="critical-risk-banner-description">
                    This assessment contains {(localAssessment.tasks || []).filter(t => t.riskRating >= 15).length} task(s) with Critical Risk (Rating 15+). 
                    <strong> Controls must be reassessed and improved before this assessment can be submitted for approval.</strong>
                  </p>
                  <p className="critical-risk-banner-recommendation">
                    <strong>Recommended Actions:</strong> Review Engineering controls (ENG), Administrative controls (ADMIN), 
                    and Personal Protective Equipment (PPE) to reduce risk levels below 15.
                  </p>
                </div>
              </div>
            </div>
          )}

          <form
            onSubmit={e => handleSubmit(e, false)}
            className="form-content"
          >
            <div className="form-grid form-grid-2">
              <div className="form-group">
                <label className="form-label">Assessment Title</label>
                <input
                  type="text"
                  value={localAssessment.title || ''}
                  onChange={e => setLocalAssessment({ ...localAssessment, title: e.target.value })}
                  className="form-input"
                  readOnly={isReadOnly}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Department</label>
                <select
                  value={localAssessment.department}
                  onChange={(e) => setLocalAssessment({ ...localAssessment, department: e.target.value })}
                  className="form-select"
                  disabled={isReadOnly}
                >
                  {departmentOptions.map(dept => (
                    <option key={dept} value={dept}>{getDepartmentDisplayName(dept)}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Job Positions</label>
              <select
                multiple
                value={localAssessment.jobPositions || []}
                onChange={e => {
                  const selected = Array.from(e.target.selectedOptions, opt => opt.value);
                  setLocalAssessment({ ...localAssessment, jobPositions: selected });
                }}
                className="form-select"
                disabled={isReadOnly}
                style={{ minHeight: 80 }}
              >
                <option value="ALL_TOWN">All Job Positions for the Town</option>
                <option value={`ALL_${localAssessment.department}`}>All Job Positions within {getDepartmentDisplayName(localAssessment.department)}</option>
                {jobPositionOptions.map(pos => (
                  <option key={pos} value={pos}>{pos}</option>
                ))}
              </select>
              <small>Hold Ctrl (Windows) or Cmd (Mac) to select multiple.</small>
            </div>

            <div className="form-group">
              <label className="form-label">Assessment Description</label>
              <textarea
                value={localAssessment.description || ''}
                onChange={e => setLocalAssessment({ ...localAssessment, description: e.target.value })}
                className="form-textarea"
                readOnly={isReadOnly}
              />
            </div>

            {canAssignDelegate && (
              <div className="form-group">
                <label className="form-label">Delegate Approver (optional)</label>
                <select
                  value={localAssessment.delegate || ''}
                  onChange={e => setLocalAssessment({ ...localAssessment, delegate: e.target.value })}
                  className="form-select"
                  disabled={isReadOnly}
                >
                  <option value="">-- Select Delegate --</option>
                  {(Array.isArray(departmentUsers) ? departmentUsers : [])
                    .filter(u =>
                      u.email &&
                      u.email.toLowerCase() !== approverEmail.toLowerCase() &&
                      u.email.toLowerCase() !== 'cao@diamondvalley.town'
                    )
                    .map(u => (
                      <option key={u.email} value={u.email}>
                        {u.email} {(u.FIRST_NAME || u.LAST_NAME) ? `(${u.FIRST_NAME || ''} ${u.LAST_NAME || ''})` : ''}
                      </option>
                    ))}

                  {Array.isArray(departmentUsers) && departmentUsers.length === 0 && (
                    <option value="" disabled>No users available</option>
                  )}
                </select>
                <small>Delegate cannot be the supervisor or CAO.</small>
              </div>
            )}

            {/* Enhanced Review Date Field with Risk-Based Information */}
            <div className="form-group">
              <label className="form-label">
                Next Review Date
                <span className="review-date-info">
                  <span className="auto-date-display">
                    Auto: {autoReviewDateStr} ({getReviewPeriodText(autoReviewInfo.period)})
                  </span>
                  <span className={`risk-reason-badge risk-reason-${autoReviewInfo.riskLevel >= 15 ? 'critical' : autoReviewInfo.riskLevel >= 12 ? 'high' : autoReviewInfo.riskLevel >= 9 ? 'moderate' : 'low'}`}>
                    {autoReviewInfo.reason}
                  </span>
                </span>
              </label>
              <input
                type="date"
                value={localAssessment.reviewDate || autoReviewDateStr}
                min={new Date().toISOString().split('T')[0]}
                max={autoReviewDateStr}
                onChange={handleReviewDateChange}
                className="form-input"
                disabled={!isSafety || isReadOnly}
              />
              {isSafety && (
                <small>
                  You may set an earlier review date, but not later than {autoReviewDateStr}.
                  <br />
                  <strong>Review periods are automatically calculated based on risk levels:</strong>
                  <br />
                  • Critical Risk (15+): 6 months | High Risk (12-14): 1 year | Moderate Risk (9-11): 1 year | Low Risk (below 9): 2 years
                </small>
              )}
              {!isSafety && (
                <small>
                  Only the authorized safety/leadership group can change the review date.
                  <br />
                  Review periods are automatically set based on the highest risk level in this assessment.
                </small>
              )}
            </div>

            <div className="tasks-section">
              <div className="tasks-header">
                <h3 className="tasks-title">Tasks and Hazards</h3>
                {hasCriticalRisk && (
                  <div className="critical-risk-summary">
                    <AlertCircle className="critical-risk-summary-icon" />
                    <span className="critical-risk-summary-text">
                      {(localAssessment.tasks || []).filter(t => t.riskRating >= 15).length} Critical Risk Task(s) Detected
                    </span>
                  </div>
                )}
                {!isReadOnly && (
                  <button
                    type="button"
                    onClick={addTask}
                    className="btn btn-primary btn-sm btn-with-icon"
                  >
                    <PlusCircle className="icon-sm" />
                    Add Task
                  </button>
                )}
              </div>

              {/* Risk Matrix Reference Card */}
              <div className="risk-matrix-reference">
                <h4 className="reference-title">Risk Matrix Reference</h4>
                <div className="reference-grid">
                  <div className="reference-column">
                    <p className="reference-header">Severity (1-5):</p>
                    <p>1 - Uncomfortable</p>
                    <p>2 - First aid</p>
                    <p>3 - Hospital</p>
                    <p>4 - Disability</p>
                    <p>5 - Fatality</p>
                  </div>
                  <div className="reference-column">
                    <p className="reference-header">Likelihood (1-5):</p>
                    <p>1 - Almost Never (1:100,000)</p>
                    <p>2 - Unlikely (1:1,000)</p>
                    <p>3 - May (1:10)</p>
                    <p>4 - Almost Certain (1:5)</p>
                    <p>5 - Likely to Happen (1:1)</p>
                  </div>
                  <div className="reference-column">
                    <p className="reference-header">Frequency (1-5):</p>
                    <p>1 - Seasonal</p>
                    <p>2 - Monthly</p>
                    <p>3 - Twice a month</p>
                    <p>4 - Weekly</p>
                    <p>5 - Daily</p>
                  </div>
                </div>
                <div className="risk-levels-grid">
                  <div className="risk-level risk-low">3-5: Low</div>
                  <div className="risk-level risk-low-moderate">6-8: Low-Moderate</div>
                  <div className="risk-level risk-moderate">9-11: Moderate</div>
                  <div className="risk-level risk-high">12-14: High</div>
                  <div className="risk-level risk-critical">15+: Critical</div>
                </div>
              </div>

              {(localAssessment.tasks || []).map((task, index) => (
                <div key={index} className={`task-card ${task.riskRating >= 15 ? 'critical-risk-task' : ''}`}>
                  <div className="task-header">
                    <h4 className="task-title">
                      Task #{index + 1}
                      {task.riskRating >= 15 && (
                        <span className="critical-risk-badge">
                          <AlertCircle className="critical-risk-badge-icon" />
                          CRITICAL RISK
                        </span>
                      )}
                    </h4>
                    {!isReadOnly && (
                      <button
                        type="button"
                        onClick={() => removeTask(index)}
                        className="btn-remove"
                      >
                        <X className="icon-sm" />
                      </button>
                    )}
                  </div>

                  <div className="form-grid form-grid-2">
                    <div className="form-group">
                      <label className="form-label">Task Description</label>
                      <input
                        type="text"
                        value={task.task || ''}
                        onChange={e => updateTask(index, 'task', e.target.value)}
                        className="form-input"
                        readOnly={isReadOnly}
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Hazards Identified</label>
                      <input
                        type="text"
                        value={task.hazards || ''}
                        onChange={e => updateTask(index, 'hazards', e.target.value)}
                        className="form-input"
                        readOnly={isReadOnly}
                      />
                    </div>
                  </div>

                  <div className="form-grid form-grid-4">
                    <div className="form-group">
                      <label className="form-label">
                        Severity
                        <span
                          title={
                            "How severe would the worst-case outcome be?\n" +
                            "1 - Uncomfortable\n" +
                            "2 - First aid\n" +
                            "3 - Hospital\n" +
                            "4 - Disability\n" +
                            "5 - Fatality"
                          }
                          style={{ marginLeft: 6, cursor: 'help', color: '#888' }}
                          aria-label="Severity Info"
                        >&#9432;</span>
                      </label>
                      <select
                        value={task.severity}
                        onChange={(e) => updateTask(index, 'severity', e.target.value)}
                        className="form-select"
                        disabled={isReadOnly}
                      >
                        <option value="1">1 - Uncomfortable</option>
                        <option value="2">2 - First aid</option>
                        <option value="3">3 - Hospital</option>
                        <option value="4">4 - Disability</option>
                        <option value="5">5 - Fatality</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        Likelihood
                        <span
                          title={
                            "How likely is this to happen?\n" +
                            "1 - Almost Never (1:100,000)\n" +
                            "2 - Unlikely (1:1,000)\n" +
                            "3 - May (1:10)\n" +
                            "4 - Almost Certain (1:5)\n" +
                            "5 - Likely to Happen (1:1)"
                          }
                          style={{ marginLeft: 6, cursor: 'help', color: '#888' }}
                          aria-label="Likelihood Info"
                        >&#9432;</span>
                      </label>
                      <select
                        value={task.likelihood}
                        onChange={(e) => updateTask(index, 'likelihood', e.target.value)}
                        className="form-select"
                        disabled={isReadOnly}
                      >
                        <option value="1">1 - Almost Never</option>
                        <option value="2">2 - Unlikely</option>
                        <option value="3">3 - May</option>
                        <option value="4">4 - Almost Certain</option>
                        <option value="5">5 - Likely to Happen</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        Frequency
                        <span
                          title={
                            "How often is this task performed?\n" +
                            "1 - Seasonal\n" +
                            "2 - Monthly\n" +
                            "3 - Twice a month\n" +
                            "4 - Weekly\n" +
                            "5 - Daily"
                          }
                          style={{ marginLeft: 6, cursor: 'help', color: '#888' }}
                          aria-label="Frequency Info"
                        >&#9432;</span>
                      </label>
                      <select
                        value={task.frequency}
                        onChange={(e) => updateTask(index, 'frequency', e.target.value)}
                        className="form-select"
                        disabled={isReadOnly}
                      >
                        <option value="1">1 - Seasonal</option>
                        <option value="2">2 - Monthly</option>
                        <option value="3">3 - Twice a month</option>
                        <option value="4">4 - Weekly</option>
                        <option value="5">5 - Daily</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Risk Rating</label>
                      <div className={`risk-rating-display ${getRiskClass(task.riskRating)} ${task.riskRating >= 15 ? 'critical-risk-rating' : ''}`}>
                        <span className="risk-rating-value">
                          {task.riskRating} - {getRiskLabel(task.riskRating)}
                        </span>
                        {task.riskRating >= 15 && (
                          <div className="critical-risk-indicator">
                            <AlertCircle className="icon-sm critical-risk-icon" />
                            <span className="critical-risk-label">CRITICAL</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Controls</label>
                    <div className="controls-grid">
                      <div className="control-section">
                        <label className="control-label">Engineering Controls (ENG:)</label>
                        <textarea
                          value={task.engControls || ''}
                          onChange={e => updateTaskControl(index, 'engControls', e.target.value)}
                          className="form-textarea control-textarea"
                          placeholder="Describe engineering controls..."
                          readOnly={isReadOnly}
                          rows={3}
                        />
                      </div>
                      <div className="control-section">
                        <label className="control-label">Administrative Controls (ADMIN:)</label>
                        <textarea
                          value={task.adminControls || ''}
                          onChange={e => updateTaskControl(index, 'adminControls', e.target.value)}
                          className="form-textarea control-textarea"
                          placeholder="Describe administrative controls..."
                          readOnly={isReadOnly}
                          rows={3}
                        />
                      </div>
                      <div className="control-section">
                        <label className="control-label">Personal Protective Equipment (PPE:)</label>
                        <textarea
                          value={task.ppeControls || ''}
                          onChange={e => updateTaskControl(index, 'ppeControls', e.target.value)}
                          className="form-textarea control-textarea"
                          placeholder="Describe PPE requirements..."
                          readOnly={isReadOnly}
                          rows={3}
                        />
                      </div>
                    </div>
                  </div>

                  {task.riskRating >= 15 && (
                    <div className="critical-risk-warning">
                      <div className="warning-content">
                        <AlertCircle className="warning-icon" />
                        <div>
                          <p className="warning-title">⚠️ CRITICAL RISK DETECTED (Rating: {task.riskRating})</p>
                          <p className="warning-text">
                            <strong>IMMEDIATE ACTION REQUIRED:</strong> Controls must be reassessed and improved to reduce risk level below 15 before this assessment can proceed.
                          </p>
                          <p className="warning-text" style={{ marginTop: '0.5rem', fontSize: '0.875rem' }}>
                            <strong>Focus Areas:</strong> Review Engineering controls (ENG), Administrative procedures (ADMIN), and Personal Protective Equipment (PPE) requirements.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {(localAssessment.tasks || []).length === 0 && (
                <div className="empty-tasks">
                  <p className="empty-text">No tasks added yet.</p>
                  {!isReadOnly && (
                    <button
                      type="button"
                      onClick={addTask}
                      className="btn btn-primary btn-sm btn-with-icon"
                    >
                      <PlusCircle className="icon-sm" />
                      Add Task
                    </button>
                  )}
                </div>
              )}
            </div>

            {isReadOnly && (
              <div className="approval-info">
                <div className="form-grid form-grid-2">
                  <div>
                    <p className="info-item">Status: <span className="info-value">{localAssessment.status}</span></p>
                    <p className="info-item">Next Review Date: <span className="info-value">{localAssessment.reviewDate}</span></p>
                  </div>
                  <div>
                    <p className="info-item">Approved By: <span className="info-value">{localAssessment.approvedBy}</span></p>
                    <p className="info-item">Approval Date: <span className="info-value">{localAssessment.approvalDate}</span></p>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            {!isReadOnly && canManage && (
              <div className="form-header-actions" style={{ marginTop: 16 }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setView('dashboard')}
                  style={{ marginRight: 8 }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-success btn-with-icon"
                  style={{ marginRight: 8 }}
                >
                  <Save className="icon-sm" />
                  {mode === 'new' ? 'Create Assessment' : 'Update Assessment'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={e => handleSubmit(e, true)}
                >
                  Save as Draft
                </button>
              </div>
            )}

            {/* Approval Action Buttons */}
            {mode === 'edit' && localAssessment.status === 'Draft' && canManage && (
              <button
                className="btn btn-warning"
                disabled={hasCriticalRisk}
                onClick={async () => {
                  await changeStatus(localAssessment.id, 'Pending Approval');
                  setView('dashboard');
                }}
                style={{ marginTop: 16 }}
              >
                Submit for Approval
              </button>
            )}
            {hasCriticalRisk && (
              <div className="critical-risk-submit-warning">
                <AlertCircle className="critical-risk-submit-icon" />
                <div>
                  <strong>Cannot submit: Critical risk must be reduced before submission.</strong>
                  <br />
                  Please reassess controls for all tasks with risk rating 15 or higher.
                </div>
              </div>
            )}

            {mode === 'view' &&
              localAssessment.status === 'Pending Approval' &&
              canApprove && (
                <div style={{ marginTop: 16 }}>
                  {hasCriticalRisk && (
                    <div className="critical-risk-approval-warning">
                      <AlertCircle className="critical-risk-approval-icon" />
                      <div>
                        <strong>⚠️ CRITICAL RISK DETECTED</strong>
                        <br />
                        This assessment cannot be approved while critical risk (15+) exists. Controls must be reassessed first.
                      </div>
                    </div>
                  )}
                  <button
                    className="btn btn-success"
                    disabled={assessment.notes?.some(n => n.type === 'update-request') || hasCriticalRisk || isApproving}
                    onClick={async () => {
                      try {
                        setIsApproving(true);
                        console.log('=== APPROVAL BUTTON CLICKED ===');
                        console.log('Assessment ID:', localAssessment.id);
                        console.log('User:', user.username);
                        console.log('Current Status:', localAssessment.status);
                        console.log('Delegate:', localAssessment.delegate);
                        
                        // H&S Advisor, delegate, and supervisor can fully approve the assessment
                        // If a delegate is assigned, they act as the supervisor for approval
                        await changeStatus(
                          localAssessment.id,
                          'Approved',
                          user.username,
                          new Date().toISOString().split('T')[0],
                          localAssessment.delegate || ''
                        );
                        
                        console.log('Status change completed, returning to dashboard');
                        setView('dashboard');
                      } catch (error) {
                        console.error('Error during approval:', error);
                        alert('Error occurred during approval. Please try again.');
                      } finally {
                        setIsApproving(false);
                      }
                    }}
                    style={{ marginRight: 8 }}
                  >
                    {isApproving ? 'Approving...' : 
                     isSafety ? 'Approve (as Authorized User)' : 
                     isDelegate ? 'Approve (as Delegate)' : 
                     'Approve'}
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={async () => {
                      await changeStatus(localAssessment.id, 'Update Requested', user.username, new Date().toISOString().split('T')[0], localAssessment.delegate);
                      setView('dashboard');
                    }}
                  >
                    Request Update
                  </button>
                  {assessment.notes?.some(n => n.type === 'update-request') && (
                    <div style={{ color: '#b85c00', marginTop: 8, fontWeight: 'bold' }}>
                      Cannot approve: There are outstanding update requests.
                    </div>
                  )}
                </div>
              )}

            {mode === 'edit' && localAssessment.status === 'Update Requested' && canManage && (
              <div>
                <button
                  className="btn btn-warning"
                  disabled={hasCriticalRisk}
                  onClick={async () => {
                    await changeStatus(localAssessment.id, 'Pending Approval');
                    setView('dashboard');
                  }}
                  style={{ marginTop: 16 }}
                >
                  Resubmit for Approval
                </button>
                {hasCriticalRisk && (
                  <div className="critical-risk-submit-warning" style={{ marginTop: 8 }}>
                    <AlertCircle className="critical-risk-submit-icon" />
                    <div>
                      <strong>Cannot resubmit: Critical risk must be reduced before resubmission.</strong>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* --- Enhanced Approval History --- */}
            {localAssessment.approvalHistory && localAssessment.approvalHistory.length > 0 && (
              <div className="approval-history" style={{ marginTop: 24 }}>
                <h4>Approval Timeline</h4>
                <div className="approval-timeline">
                  {localAssessment.approvalHistory.map((entry, idx) => {
                    let roleLabel = '';
                    let roleClass = 'role-general';
                    let actionIcon = '•';
                    
                    // Determine role and styling
                    if (entry.delegate) {
                      roleLabel = 'Delegate';
                      roleClass = 'role-delegate';
                      actionIcon = '👤';
                    } else if (entry.by && entry.by.toLowerCase() === departmentSupervisor.FIELD_SUPERVISOR?.toLowerCase()) {
                      roleLabel = 'Supervisor';
                      roleClass = 'role-supervisor';
                      actionIcon = '👮';
                    } else if (entry.by && entry.by.toLowerCase() === 'safety@diamondvalley.town') {
                      roleLabel = 'H&S Advisor';
                      roleClass = 'role-safety';
                      actionIcon = '🛡️';
                    } else if (entry.by && entry.by.toLowerCase() === 'cao@diamondvalley.town') {
                      roleLabel = 'CAO';
                      roleClass = 'role-cao';
                      actionIcon = '👔';
                    }

                    // Enhanced action descriptions
                    let actionDescription = entry.action;
                    if (entry.action === 'Pending Approval') {
                      actionDescription = 'Submitted for Approval';
                    } else if (entry.action === 'Update Requested') {
                      actionDescription = 'Requested Updates';
                    }

                    return (
                      <div key={idx} className="timeline-entry">
                        <div className="timeline-marker">
                          <span className="timeline-icon">{actionIcon}</span>
                        </div>
                        <div className="timeline-content">
                          <div className="timeline-action">
                            <strong>{actionDescription}</strong>
                            {roleLabel && (
                              <span className={`role-badge ${roleClass}`}>
                                {roleLabel}
                              </span>
                            )}
                          </div>
                          <div className="timeline-details">
                            <span className="timeline-user">{entry.by}</span>
                            <span className="timeline-date">{new Date(entry.date).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Workflow Status Summary */}
                <div className="workflow-summary" style={{ marginTop: 16, padding: 12, background: '#f8fafc', borderRadius: 4, border: '1px solid #e2e8f0' }}>
                  <strong>Current Status:</strong> {localAssessment.status}
                  {localAssessment.delegate && (
                    <div style={{ marginTop: 4, fontSize: '0.9em', color: '#64748b' }}>
                      <strong>Assigned Delegate:</strong> {localAssessment.delegate}
                    </div>
                  )}
                </div>
              </div>
            )}
          </form>

          {/* Place the notes section OUTSIDE the form */}
          <div className="notes-section">
            <h3>Health & Safety Advisor Notes</h3>
            <ul>
              {(assessment.notes || []).map((note, idx) => (
                <li key={idx} style={{marginBottom: 8}}>
                  <strong>{note.user}</strong>
                  <span style={{color:'#888', marginLeft: 8}}>
                    {note.date ? new Date(note.date).toLocaleString() : ''}
                  </span>
                  <span style={{
                    marginLeft: 8,
                    fontWeight: note.type === 'update-request' ? 'bold' : 'normal',
                    color: note.type === 'update-request' ? '#b85c00' : '#333'
                  }}>
                    [{note.type === 'update-request' ? 'Update Request' : 'Note'}]
                  </span>
                  <div>{note.message}</div>
                </li>
              ))}
            </ul>
            {!isReadOnly && assessment.id && (
              <form
                onSubmit={async e => {
                  e.preventDefault();
                  if (!noteInput.trim()) return;
                  const newNote = {
                    user: user.username,
                    type: noteType,
                    message: noteInput
                  };
                  await addNote(assessment.id, newNote);
                  setNoteInput('');
                }}
                style={{marginTop: 12}}
              >
                <textarea
                  value={noteInput}
                  onChange={e => setNoteInput(e.target.value)}
                  placeholder="Add a note or update request..."
                  rows={2}
                  style={{width: '100%', marginBottom: 6}}
                />
                <div>
                  <select value={noteType} onChange={e => setNoteType(e.target.value)} style={{marginRight: 8}}>
                    <option value="note">Note</option>
                    <option value="update-request">Update Request</option>
                  </select>
                  <button type="submit" className="btn btn-primary btn-sm">Add</button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  };

  // --- Main Render ---
  return (
    <div className="app-container">
      <header className="app-header">
        <div className="header-content">
          <div className="header-info">
            <center>
              <h1 className="app-title">Formal Hazard Assessment Workflow</h1>
            </center>
          </div>
        </div>
      </header>

      <main className="main-content">
        {view === 'dashboard' && renderDashboard()}

        {(view === 'new' || view === 'edit' || view === 'view') && currentAssessment && (
          <AssessmentForm
            assessment={currentAssessment}
            setAssessment={setCurrentAssessment}
            mode={view}
            user={user}
          />
        )}
      </main>
    </div>
  );
}
